import React, { useCallback, useMemo, useState } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { rightDrawerOptions } from "./projectTools";
import LandingPage from "./landingPage";
import Drawer from "components/basic/drawer";
import Header from "components/basic/header";
import SiteWrapper from "./siteWrapper";
import Calculator from "./calculator";
import { useLogoutMutation, useMeQuery } from "generated/graphql";
import Typography from "components/basic/typography";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import ChargerEstimator from "./chargerEstimator";
import SignIn from "./userAuthentication/signIn";
import SignUp from "./userAuthentication/signUp";
import EvServiceInstallation from "./dashboard/pages/serviceOffering/evInstallationService";
import Dashboard from "./dashboard";
import ChatPopups from "./dashboard/pages/message/components/ChatPopups";
import { useAnalytics } from "analytics/useAnalytics";
import { useLanguageSync } from "hooks/useLanguage";
import Privacy from "./privacy";
import Terms from "./terms";
import VendorAgreement from "./vendorAgreement";
import VendorStatusBanner from "./components/VendorStatusBanner";
import { clearEstimatorDrafts } from "./chargerEstimator/helpers/estimatorDraft";

const Pages: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [, logout] = useLogoutMutation();
  const [rightDrawerOpen, setRightDrawerOpen] = useState(false);
  const [{ data, fetching }] = useMeQuery();

  const location = useLocation();
  const currentPath = location.pathname;

  useAnalytics();
  useLanguageSync();

  const userLoggedIn = useMemo(() => {
    if (fetching) {
      return false;
    }

    if (!data?.me) {
      return false;
    }

    return true;
  }, [data, fetching]);

  const drawerHeader = useMemo(() => {
    if (fetching) {
      return <div>{t("status.loading")}</div>;
    }

    if (!data?.me) {
      return;
    }

    const user = data.me;

    return (
      <div
        style={{ display: "flex", alignItems: "center", gap: theme.spacing(2) }}
      >
        <AccountCircleIcon />
        <div>
          <Typography variant="body1">
            {user.firstName} {user.lastName}
          </Typography>
          <Typography variant="body2">{user.email}</Typography>
        </div>
      </div>
    );
  }, [data, fetching, theme, t]);

  const handleLogout = useCallback(async () => {
    await logout({});
    clearEstimatorDrafts();
    navigate("/signin");
  }, [logout, navigate]);

  return (
    <ChatPopups>
      <Header onMenuClick={() => setRightDrawerOpen((prev) => !prev)} />
      <VendorStatusBanner />

      <Drawer
        title={drawerHeader}
        open={rightDrawerOpen}
        onClose={() => setRightDrawerOpen(false)}
        drawerList={[
          ...(!userLoggedIn
            ? [
                {
                  label: t("nav.login_register").toUpperCase(),
                  path: "/signin",
                },
              ]
            : []), // only show if user is not logged in
          ...rightDrawerOptions(t),
          ...(userLoggedIn
            ? [{ label: t("nav.logout").toUpperCase(), onClick: handleLogout }]
            : []),
        ]}
      />

      {["/register", "/signin", "/signup"].includes(currentPath) ? (
        <Routes>
          <Route path="/signin" element={<SignIn />} />
          <Route path="/signup" element={<SignUp />} />
        </Routes>
      ) : (
        <SiteWrapper>
          <Routes>
            <Route
              path="/dashboard/ServiceInstallation"
              element={<EvServiceInstallation />}
            />
            <Route path="/dashboard/*" element={<Dashboard />} />
            <Route path="/" element={<LandingPage />} />
            <Route path="/calculator" element={<Calculator />} />
            <Route
              path="/chargerEstimator"
              element={<ChargerEstimator account={data?.me} />}
            />
            <Route path="/browser" element={<div>Browser</div>} />
            <Route path="/incentives" element={<div>incentives</div>} />
            <Route path="/industryMap" element={<div>Industry Map</div>} />
            <Route
              path="/serviceProviders"
              element={<div>Service Providers</div>}
            />
            <Route
              path="/sustainabilityTracker"
              element={<div>sustainability tracker</div>}
            />
            <Route path="/about" element={<div>about</div>} />
            <Route path="/contact" element={<div>contact</div>} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/vendor-agreement" element={<VendorAgreement />} />

            <Route path="*" element={<div>{t("status.not_found")}</div>} />
          </Routes>
        </SiteWrapper>
      )}
    </ChatPopups>
  );
};

export default Pages;
